import { isApolloError } from '@apollo/client';

import { MIN_PASSWORD_LENGTH } from './PasswordPolicy';

enum AuthException {
  AuthException = 'AuthException',
  AuthRateLimitExceededException = 'AuthRateLimitExceededException',
  AuthInvalidPasswordException = 'AuthInvalidPasswordException',
  AuthUsernameExistsException = 'AuthUsernameExistsException',
  AuthUserNotFoundException = 'AuthUserNotFoundException',
  AuthLoginFailedException = 'AuthLoginFailedException',
  AuthTokenNotFoundException = 'AuthTokenNotFoundException',
}

export const AuthExceptionMessage: Record<AuthException, string> = {
  [AuthException.AuthRateLimitExceededException]:
    'Too many attempts. Please wait before trying again.',
  [AuthException.AuthException]:
    'There was an error during login. Please try again.',
  [AuthException.AuthInvalidPasswordException]: `The password must be at least ${MIN_PASSWORD_LENGTH} characters long.`,
  [AuthException.AuthUsernameExistsException]: 'The user already exists.',
  [AuthException.AuthUserNotFoundException]: 'The email or password is wrong.',
  [AuthException.AuthLoginFailedException]: 'The email or password is wrong.',
  [AuthException.AuthTokenNotFoundException]:
    'The reset password token is invalid.',
};

/**
 * Types whose server message is specific and safe to show as-is (retry-after
 * hints, which password rule failed, ...). The static text is only a fallback.
 * The others keep the static text on purpose (e.g. not revealing whether an
 * account exists).
 */
const PREFER_SERVER_MESSAGE = new Set<string>([
  AuthException.AuthException,
  AuthException.AuthRateLimitExceededException,
  AuthException.AuthInvalidPasswordException,
]);

type GraphQLErrorDetail = {
  message?: string;
  type?: string;
  extensions?: { type?: unknown };
};

/**
 * Exception class of the first GraphQL error. Datagon (Spring for GraphQL)
 * puts it in `extensions.type`; older backends sent a top-level `type`.
 */
export const getGraphQLErrorType = (error: unknown): string | undefined => {
  if (!(error instanceof Error) || !isApolloError(error)) return undefined;
  const detail = error.graphQLErrors?.[0] as GraphQLErrorDetail | undefined;
  const type = detail?.extensions?.type ?? detail?.type;

  return typeof type === 'string' ? type : undefined;
};

/**
 * User-facing message for an auth mutation error: a friendly text for known
 * AuthException types, otherwise the server's own message, otherwise `fallback`.
 */
export const getAuthErrorMessage = (error: unknown, fallback: string) => {
  if (!(error instanceof Error) || !isApolloError(error)) return fallback;
  const detail = error.graphQLErrors?.[0] as GraphQLErrorDetail | undefined;
  const serverMessage = detail?.message || error.message;
  const type = getGraphQLErrorType(error);

  if (type && type in AuthExceptionMessage) {
    const friendly = AuthExceptionMessage[type as AuthException];

    return PREFER_SERVER_MESSAGE.has(type)
      ? serverMessage || friendly
      : friendly;
  }

  return serverMessage || fallback;
};

export default AuthException;
